import React, { useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Select from "@amzn/meridian/select";
import SelectOption from "@amzn/meridian/select/select-option";
import { Icon } from "@amzn/meridian";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import clipboardTokens from "@amzn/meridian-tokens/base/icon/clipboard";
import searchTokens from "@amzn/meridian-tokens/base/icon/search";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { setActiveSiteCode } from "src/store/userSlice";
import {
  completedInspectionStatus,
  EAP_PILOT_SITES,
  extractSiteCode,
} from "src/constants/constants";
import { isUpdateAvailable } from "src/utils/checkForWebsiteUpdates";
import { formatTime } from "src/utils/dateUtil";
import AustinInspectionCard from "./austinInspectionCard";
import "./landing.scss";

/* ── Stat Card ── */
const StatCard: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <Column className="home-card" spacingInset="400 500" spacing="200" alignmentHorizontal="start">
    <Text color="success" type="h600">
      {value}
    </Text>
    <Text type="b200" color="secondary">
      {label}
    </Text>
  </Column>
);

/* ── Workflow Card ── */
interface HomeCardProps {
  icon: object;
  title: string;
  description: string;
  onClick: () => void;
}

const HomeCard: React.FC<HomeCardProps> = ({ icon, title, description, onClick }) => (
  <div className="home-card" onClick={onClick}>
    <Row alignmentVertical="center" spacing="400" spacingInset="400" alignmentHorizontal="justify">
      <Row className=" home-workflow-icon-bg" spacingInset="400">
        <Icon tokens={icon} color="success" />
      </Row>
      <Column spacing="100" width="fill">
        <Text type="h200">{title}</Text>
        <Text type="b200" color="secondary">
          {description}
        </Text>
      </Column>
      <Icon tokens={chevronRightSmallTokens} />
    </Row>
  </div>
);

/* ── Recent Activity Item ── */
interface RecentItemProps {
  icon: object;
  title: string;
  subtitle: string;
  subtitleColor?: any;
  time: string;
}

const RecentItem: React.FC<RecentItemProps> = ({ icon, title, subtitle, subtitleColor, time }) => (
  <Row alignmentVertical="top" spacingInset="400" widths={["fit", "fill", "fit"]}>
    <Row spacingInset="300" className="home-recent-icon-bg">
      <Icon tokens={icon} />
    </Row>
    <Column spacing="none">
      <Text type="b300">{title}</Text>
      <Text type="b100" color={subtitleColor}>
        {subtitle}
      </Text>
    </Column>
    <Text type="b100" color="secondary">
      {time}
    </Text>
  </Row>
);

/* ── Main Home Component ── */
interface LandingHomeProps {
  austinEnabled?: boolean;
}

const LandingHome: React.FC<LandingHomeProps> = ({ austinEnabled = true }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { siteLocation, activeSiteCode } = useAppSelector((state) => state.user);

  // the EAP_SITES list. If matched, select it; otherwise fall back to the first.
  // Guard: skip if activeSiteCode already set (user may have manually selected a site).
  useEffect(() => {
    if (activeSiteCode) return;
    if (!siteLocation) {
      // No site location available — fall back to the first pilot site.
      dispatch(setActiveSiteCode(EAP_PILOT_SITES[0].code));
      return;
    }
    const siteCode = extractSiteCode(siteLocation);
    const isSiteInPilot = EAP_PILOT_SITES.find((s) => s.code === siteCode);
    dispatch(setActiveSiteCode(isSiteInPilot ? siteCode : EAP_PILOT_SITES[0].code));
  }, [siteLocation, activeSiteCode, dispatch]);

  useEffect(() => {
    if (isUpdateAvailable()) window.location.reload();
  }, []);
  const { userInspections } = useAppSelector((state) => state.inspection);
  const { flags } = useAppSelector((state) => state.featureFlags);

  const recentInspections = [...userInspections]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 2);

  return (
    <Column spacing="500" spacingInset="none 500 500">
      {/* Site selector */}
      <Select
        value={activeSiteCode}
        onChange={(value: string) => dispatch(setActiveSiteCode(value))}
        placeholder="Select a site"
        width="100%"
      >
        {EAP_PILOT_SITES.map((site) => (
          <SelectOption key={site.code} value={site.code} label={site.label} />
        ))}
      </Select>

      {/* Stats row — placeholder values for now; will be replaced with real stats values going forward. */}
      {/* will add it going further once we have concrete logic. commenting out for now*/}

      {/* <Row widths={["fill", "fill"]} spacing="400">
        <StatCard value={12} label="Inspections today" />
        <StatCard value={45} label="Products classified" />
      </Row> */}

      {/* Workflows */}
      <Column spacing="400">
        <Text type="h300">Workflows</Text>
        <HomeCard
          icon={searchTokens}
          title="Unsellable Waste Product Classification"
          description="AI-based waste profile identification"
          onClick={() => navigate("/classification/scan")}
        />

        {austinEnabled && activeSiteCode ? (
          <AustinInspectionCard siteCode={activeSiteCode} />
        ) : (
          <HomeCard
            icon={clipboardTokens}
            title="Cage Inspection"
            description="AI-guided visual compliance check"
            onClick={() => {
              navigate("/configurationDrivenInspections");
            }}
          />
        )}
      </Column>

      {/* Recent */}
      {recentInspections.length > 0 && (
        <Column spacing="400">
          <Text type="h300">Recent</Text>
          <Column className="home-card" spacing="none">
            {recentInspections.map((inspection) => {
              const isCompleted = inspection.status === completedInspectionStatus;
              return (
                <RecentItem
                  key={inspection.inspectionId}
                  icon={clipboardTokens}
                  title={inspection.templateName}
                  subtitle={inspection.status}
                  subtitleColor={isCompleted ? "success" : "warning"}
                  time={formatTime(inspection.createdAt)}
                />
              );
            })}
          </Column>
        </Column>
      )}
    </Column>
  );
};

export default LandingHome;
